/* eslint-disable react/no-unstable-nested-components, react-hooks/static-components, react-hooks/immutability */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Users, Briefcase, TrendingUp, FileText, Download, Mail, Activity, ArrowUp, ArrowDown, Minus, ShieldCheck, UserPlus, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;

const AdministratorDashboard = ({ user }) => {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matchingHealth, setMatchingHealth] = useState(null);



  useEffect(() => {
    fetchDashboardData();
    fetchMatchingHealth();
  }, []);

  const fetchMatchingHealth = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/matching/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMatchingHealth(res.data);
    } catch (err) {
      // Non-fatal — just hide the card.
      setMatchingHealth(null);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleDownloadSourcePackage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/source-code-package/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'social_impact_platform_source_code.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Source package downloaded successfully!');
    } catch (error) {
      console.error('Error downloading source package:', error);
      toast.error('Failed to download source package');
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <Card className="hover-lift transition-all duration-300" data-testid={`admin-stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <span className="block text-sm font-medium text-gray-600">{title}</span>
            <span className={`block text-2xl font-bold text-${color}-600`}>{value}</span>
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-8 text-white">
        <h2 className="text-3xl font-bold mb-2">
          {t('dashboard.admin_welcome', { name: user?.username || t('roles.administrator') })}
        </h2>
        <p className="text-blue-100">{t('dashboard.admin_subtitle')}</p>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('dashboard.stat_total_projects')}
          value={dashboardData?.total_projects || 0}
          icon={Briefcase}
          color="blue"
        />
        <StatCard
          title={t('dashboard.stat_active_users')}
          value={dashboardData?.active_users || 0}
          icon={Users}
          color="green"
        />
        <StatCard
          title={t('dashboard.stat_total_budget')}
          value={formatCurrency(dashboardData?.total_budget || 0)}
          icon={TrendingUp}
          color="purple"
        />
        <StatCard
          title={t('dashboard.stat_documents')}
          value={dashboardData?.total_documents || 0}
          icon={FileText}
          color="orange"
        />
      </div>

      {/* Matching Health — live histogram + week-over-week delta */}
      {matchingHealth && matchingHealth.current && (
        <MatchingHealthCard health={matchingHealth} />
      )}

      {/* Download Section - HIDDEN per user request */}
      {/* <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-1">Platform Source Code</h3>
              <p className="text-sm text-gray-600">Download the complete source code package as a ZIP file</p>
            </div>
            <Button 
              onClick={handleDownloadSourcePackage}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Source Package
            </Button>
          </div>
        </CardContent>
      </Card> */}

      {/* Recent Projects */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t('dashboard.recent_projects')}</h3>
          <div className="space-y-3">
            {dashboardData?.recent_projects?.slice(0, 5).map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div>
                  <Link
                      to={`/projects/${project.id}`}
                      className="font-medium text-blue-700 hover:text-blue-900"
                      data-testid={`admin-project-link-${project.id}`}
                    >
                      {project.title || project.name || `Project ${project.id}`}
                    </Link>
                  <p className="text-sm text-gray-500">{t('dashboard.status_label', { status: project.status })}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{formatCurrency(project.budget || 0)}</p>
                  <p className="text-xs text-gray-500">{project.owner_name}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/admin/users">
          <Card className="hover-lift cursor-pointer transition-all duration-300">
            <CardContent className="p-6 text-center">
              <Users className="h-12 w-12 mx-auto mb-3 text-blue-600" />
              <h3 className="font-semibold">{t('dashboard.user_management')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('dashboard.user_management_desc')}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/reports">
          <Card className="hover-lift cursor-pointer transition-all duration-300">
            <CardContent className="p-6 text-center">
              <FileText className="h-12 w-12 mx-auto mb-3 text-green-700" />
              <h3 className="font-semibold">{t('dashboard.platform_reports')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('dashboard.platform_reports_desc')}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/contact-requests" data-testid="admin-contact-requests-link">
          <Card className="hover-lift cursor-pointer transition-all duration-300">
            <CardContent className="p-6 text-center">
              <Mail className="h-12 w-12 mx-auto mb-3 text-emerald-700" />
              <h3 className="font-semibold">{t('dashboard.contact_inbox', 'Contact Inbox')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('dashboard.contact_inbox_desc', 'Lead enquiries from the public landing page')}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/settings">
          <Card className="hover-lift cursor-pointer transition-all duration-300">
            <CardContent className="p-6 text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 text-purple-600" />
              <h3 className="font-semibold">{t('dashboard.platform_settings')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('dashboard.platform_settings_desc')}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/pending-registrations" data-testid="admin-pending-registrations-link">
          <Card className="hover-lift cursor-pointer transition-all duration-300">
            <CardContent className="p-6 text-center">
              <UserPlus className="h-12 w-12 mx-auto mb-3 text-amber-600" />
              <h3 className="font-semibold">{t('dashboard.pending_registrations', 'Pending Registrations')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('dashboard.pending_registrations_desc', 'Review Social Actors & Investors awaiting approval')}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/permissions" data-testid="admin-permissions-link">
          <Card className="hover-lift cursor-pointer transition-all duration-300">
            <CardContent className="p-6 text-center">
              <ShieldCheck className="h-12 w-12 mx-auto mb-3 text-blue-600" />
              <h3 className="font-semibold">{t('dashboard.permissions_matrix', 'Permissions Matrix')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('dashboard.permissions_matrix_desc', 'Read-only RBAC map and .docx export for auditors')}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/restore-status" data-testid="admin-restore-status-link">
          <Card className="hover-lift cursor-pointer transition-all duration-300">
            <CardContent className="p-6 text-center">
              <Database className="h-12 w-12 mx-auto mb-3 text-blue-600" />
              <h3 className="font-semibold">{t('dashboard.data_sync_status', 'Data Sync Status')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('dashboard.data_sync_status_desc', 'Compare bundled fixture against live DB; force-restore missing modules')}</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdministratorDashboard;

// ──────────────────────────────────────────────────────────────────────
// Matching Health card — live 5-bucket histogram + WoW delta.
// ──────────────────────────────────────────────────────────────────────
const BUCKET_META = [
  { key: 'Excellent Match', label: 'Excellent',  colour: 'bg-emerald-700' },
  { key: 'Good Match',      label: 'Good',       colour: 'bg-lime-500'    },
  { key: 'Moderate Match',  label: 'Moderate',   colour: 'bg-amber-400'   },
  { key: 'Weak Match',      label: 'Weak',       colour: 'bg-orange-500'  },
  { key: 'Poor Match',      label: 'Poor',       colour: 'bg-red-600'     },
];

const DeltaPill = ({ value }) => {
  const n = Number(value) || 0;
  if (n === 0) {
    return <span className="inline-flex items-center text-xs text-gray-500 gap-1"><Minus className="h-3 w-3" /> 0</span>;
  }
  const up = n > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  // For this card, an increase in any bucket is neutral except Excellent/Good
  // (positive) and Weak/Poor (negative). Colour is applied by the caller.
  return (
    <span className={`inline-flex items-center text-xs gap-1 font-semibold ${up ? 'text-emerald-700' : 'text-red-600'}`}>
      <Icon className="h-3 w-3" /> {up ? '+' : ''}{n}
    </span>
  );
};

const MatchingHealthCard = ({ health }) => {
  const { current, delta_vs_week_ago, has_baseline, compared_to } = health;
  const buckets = current.buckets || {};
  const deltas = (delta_vs_week_ago && delta_vs_week_ago.buckets) || {};
  const max = Math.max(1, ...BUCKET_META.map((b) => buckets[b.key] || 0));

  // Delta colour rule: for Excellent/Good, up = good (emerald); for Weak/Poor,
  // up = bad (red). Moderate shown neutral.
  const colourDelta = (key, value) => {
    if (value === 0) return 'text-gray-500';
    if (key === 'Excellent Match' || key === 'Good Match') return value > 0 ? 'text-emerald-700' : 'text-red-600';
    if (key === 'Weak Match' || key === 'Poor Match')       return value > 0 ? 'text-red-600'    : 'text-emerald-700';
    return 'text-gray-500';
  };

  return (
    <Card data-testid="matching-health-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Matching Health</h3>
          </div>
          <Link to="/supply-demand-matching" className="text-xs text-blue-600 hover:underline">
            Open Supply & Demand →
          </Link>
        </div>

        {/* Headline stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Actors × Investors</p>
            <span className="block text-2xl font-bold text-gray-900">{(current.pair_count || 0).toLocaleString('el-GR')}</span>
            <p className="text-xs text-gray-500">{current.actor_count} actors · {current.investor_count} investors</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Avg. score</p>
            <span className="block text-2xl font-bold text-gray-900">{(current.avg_score || 0).toFixed(1)}<span className="text-sm text-gray-500"> / 100</span></span>
            {has_baseline && (
              <p className={`text-xs ${delta_vs_week_ago.avg_score > 0 ? 'text-emerald-700' : delta_vs_week_ago.avg_score < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                {delta_vs_week_ago.avg_score > 0 ? '▲ ' : delta_vs_week_ago.avg_score < 0 ? '▼ ' : '— '}
                {delta_vs_week_ago.avg_score > 0 ? '+' : ''}{delta_vs_week_ago.avg_score} vs. 7 days ago
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">High-quality pairs</p>
            <span className="block text-2xl font-bold text-emerald-700">{(current.high_quality || 0).toLocaleString('el-GR')}</span>
            {has_baseline && (
              <p className="text-xs">
                <span className={delta_vs_week_ago.high_quality > 0 ? 'text-emerald-700' : delta_vs_week_ago.high_quality < 0 ? 'text-red-600' : 'text-gray-500'}>
                  {delta_vs_week_ago.high_quality > 0 ? '▲ +' : delta_vs_week_ago.high_quality < 0 ? '▼ ' : '— '}
                  {delta_vs_week_ago.high_quality !== 0 ? delta_vs_week_ago.high_quality : 0}
                </span>
                <span className="text-gray-500"> vs. 7 days ago</span>
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Baseline</p>
            {has_baseline ? (
              <p className="text-sm font-medium text-gray-800 mt-1">Week of {compared_to}</p>
            ) : (
              <p className="text-sm font-medium text-amber-700 mt-1">Building baseline…</p>
            )}
            <p className="text-xs text-gray-500 mt-0.5">Snapshot taken daily</p>
          </div>
        </div>

        {/* Histogram */}
        <div className="space-y-2">
          {BUCKET_META.map((b) => {
            const count = buckets[b.key] || 0;
            const pct = (count / max) * 100;
            const d = deltas[b.key] || 0;
            return (
              <div key={b.key} className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-700 w-20">{b.label}</span>
                <div className="flex-1 bg-gray-100 rounded h-6 relative overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 ${b.colour} transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-semibold text-gray-800">
                    {count}
                  </span>
                </div>
                <span className={`text-xs font-semibold w-12 text-right ${colourDelta(b.key, d)}`}>
                  {has_baseline ? (d === 0 ? '—' : `${d > 0 ? '+' : ''}${d}`) : '·'}
                </span>
              </div>
            );
          })}
        </div>

        {!has_baseline && (
          <p className="text-xs text-gray-500 mt-4">
            Week-over-week deltas will appear once 7 days of snapshots have accumulated.
          </p>
        )}
      </CardContent>
    </Card>
  );
};